import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Download, CircleCheck, ChevronDown, Upload, CircleAlert } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { SectionTitle } from "../components/SectionTitle";
import { ASSOC } from "../data/associacao";
import { PARCEIROS_INSTITUCIONAIS, PATROCINADORES } from "../data/parceiros";
import { track } from "../analytics";
import estatutosPt from "../data/estatutos/pt.json";
import estatutosEn from "../data/estatutos/en.json";
import estatutosDe from "../data/estatutos/de.json";
import estatutosFr from "../data/estatutos/fr.json";
import estatutosIt from "../data/estatutos/it.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ESTATUTOS = { pt: estatutosPt, en: estatutosEn, de: estatutosDe, fr: estatutosFr, it: estatutosIt };
const MAX_FICHEIRO = 10 * 1024 * 1024;
const TIPOS_OK = ["application/pdf", "image/jpeg", "image/png"];

const MEMBROS_DIRECAO = [
    { nome: "Paula Angelino", cargoKey: "cargoPaulaAngelino", notaKey: "paulaAngelinoNota", testid: "assoc-membro-paula-angelino" },
    { nome: "Cristina Moita", cargoKey: "cargoCristina", notaKey: "cristinaNota", testid: "assoc-membro-cristina-moita" },
];
const MEMBROS_FUNDADORES = [
    { nome: "Paula Silva", cargoKey: "cargoPaulaSilva", notaKey: "paulaSilvaNota", testid: "assoc-membro-paula-silva" },
    { nome: "Paulo Nunes", cargoKey: "cargoPaulo", notaKey: "pauloNota", testid: "assoc-membro-paulo-nunes" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const ParceiroCard = ({ p, titulo, sub }) => {
    const [falhou, setFalhou] = useState(false);
    const inner = (
        <>
            <div className="flex items-end justify-center h-24">
                {p.logo && !falhou ? (
                    <img loading="lazy" decoding="async" src={p.logo} alt={p.nome}
                        className="h-20 w-auto max-w-full object-contain shadow-[0_10px_30px_-14px_rgba(0,0,0,0.9)] group-hover:scale-[1.03] transition-transform duration-500"
                        onError={() => setFalhou(true)} />
                ) : (
                    <span className="font-display text-lg text-[#7a5c1e] text-center leading-snug px-2">{p.nome}</span>
                )}
            </div>
            <p className="font-display font-light text-base text-[#efe6d6] mt-3.5 text-center">{p.nome}</p>
            <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#c9a24b]/80 mt-1.5 text-center">{titulo}</p>
            {sub && <p className="font-caps text-[8px] tracking-[0.2em] uppercase text-[#efe6d6]/45 mt-1 text-center">{sub}</p>}
        </>
    );
    const cls = "flex flex-col items-center";
    return p.url ? (
        <a href={p.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`parceiro-${p.id}`}>{inner}</a>
    ) : (
        <div className={cls} data-testid={`parceiro-${p.id}`}>{inner}</div>
    );
};

const MembroCard = ({ m, t }) => (
    <div className="border border-[#c9a24b]/25 bg-[#0a0705]/70 p-6 sm:p-7" data-testid={m.testid}>
        <p className="font-display font-light text-xl text-[#efe6d6]" data-testid={`${m.testid}-nome`}>{m.nome}</p>
        <p className="font-caps text-[9px] tracking-[0.28em] uppercase gold-text mt-2" data-testid={`${m.testid}-cargo`}>{t.assoc.direcao[m.cargoKey]}</p>
        <p className="font-light text-sm text-[#efe6d6]/60 leading-relaxed mt-3">{t.assoc.direcao[m.notaKey]}</p>
    </div>
);

const Campo = ({ label, erro, children, testid }) => (
    <div data-testid={testid}>
        {children}
        {erro && (
            <p className="font-light text-xs text-[#e0a0a0] mt-1.5 flex items-center gap-1.5" data-testid={`${testid}-erro`}>
                <CircleAlert size={12} /> {erro}
            </p>
        )}
        <label className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mt-2 sr-only">{label}</label>
    </div>
);

export default function AssociacaoPage() {
    const { lang, t } = useLanguage();
    const a = t.assoc;
    const est = ESTATUTOS[lang] || ESTATUTOS.pt;

    useSEO({
        title: t.seo.associacao.title,
        description: t.seo.associacao.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: a.titulo, path: "/associacao" }]),
    });

    // formulário
    const vazio = { nome: "", rua: "", cp: "", localidade: "", pais: "", email: "", telefone: "", categoria: "efetivo", website: "" };
    const [form, setForm] = useState(vazio);
    const [ficheiro, setFicheiro] = useState(null);
    const [aceite1, setAceite1] = useState(false);
    const [aceite2, setAceite2] = useState(false);
    const [erros, setErros] = useState({});
    const [estado, setEstado] = useState("form"); // form | aEnviar | sucesso
    const [erroGeral, setErroGeral] = useState(null);
    const [aberto, setAberto] = useState(0); // acordeão de capítulos

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const escolherFicheiro = (e) => {
        const f = e.target.files?.[0] || null;
        setErros((er) => ({ ...er, comprovativo: null }));
        if (!f) { setFicheiro(null); return; }
        if (!TIPOS_OK.includes(f.type)) {
            setErros((er) => ({ ...er, comprovativo: a.form.erroFicheiroTipo }));
            setFicheiro(null);
            return;
        }
        if (f.size > MAX_FICHEIRO) {
            setErros((er) => ({ ...er, comprovativo: a.form.erroFicheiroGrande }));
            setFicheiro(null);
            return;
        }
        setFicheiro(f);
    };

    const validar = () => {
        const er = {};
        ["nome", "rua", "cp", "localidade", "pais", "email"].forEach((k) => {
            if (!form[k].trim()) er[k] = a.form.erroObrigatorio;
        });
        if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) er.email = a.form.erroEmail;
        if (!ficheiro) er.comprovativo = a.form.erroObrigatorio;
        if (!aceite1) er.aceite1 = a.form.erroObrigatorio;
        if (!aceite2) er.aceite2 = a.form.erroObrigatorio;
        setErros(er);
        return Object.keys(er).length === 0;
    };

    const submeter = async (e) => {
        e.preventDefault();
        setErroGeral(null);
        if (!validar()) return;
        setEstado("aEnviar");
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            fd.append("lang", lang);
            fd.append("aceite_estatutos", String(aceite1));
            fd.append("aceite_dados", String(aceite2));
            fd.append("comprovativo", ficheiro);
            await axios.post(`${API}/adesoes`, fd);
            track("Lead", { tipo: "adesao-associacao" }, { email: form.email });
            track("CompleteRegistration", { method: "adesao-associacao" }, { email: form.email });
            setEstado("sucesso");
        } catch {
            setErroGeral(a.form.erroGeral);
            setEstado("form");
        }
    };

    const famosa = ASSOC.moradaFamosa ? `Pastelaria Famosa (${ASSOC.moradaFamosa})` : "Pastelaria Famosa";
    const inputCls = "w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300";
    const labelCls = "font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5";
    const linhasDados = [
        [a.dados.denominacao, a.dados.denominacaoV],
        [a.dados.formaJuridica, a.dados.formaJuridicaV],
        [a.dados.sede, a.dados.sedeV],
        [a.dados.morada, a.dados.moradaV],
        [a.dados.constituicao, a.dados.constituicaoV],
        [a.dados.duracao, a.dados.duracaoV],
        [a.dados.uid, ASSOC.uid || a.dados.emTratamento, !ASSOC.uid],
        [a.dados.conta, ASSOC.contaBancaria || a.dados.aDesignar, !ASSOC.contaBancaria],
        [a.dados.exercicio, a.dados.exercicioV],
        [a.dados.contacto, "produktion@fadosamesa.ch"],
    ];
    const subnav = [
        ["quem-somos", a.subnav.quemSomos], ["dados", a.subnav.dados], ["direcao", a.subnav.direcao],
        ["parceiros", a.subnav.parceiros], ["quotas", a.subnav.quotas], ["adesao", a.subnav.adesao], ["estatutos", a.subnav.estatutos],
    ];

    return (
        <div className="min-h-screen bg-[#070503]" data-testid="associacao-page">
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1000px] px-5 sm:px-10 pt-44 sm:pt-32 pb-24"
            >
                <Reveal className="text-center">
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em]" data-testid="assoc-title">{a.titulo}</h1>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <p className="font-display font-light text-lg text-[#efe6d6]/70 leading-relaxed max-w-2xl mx-auto mt-6" data-testid="assoc-intro">{a.intro}</p>
                </Reveal>

                <Reveal delay={0.1}>
                    <nav className="flex flex-wrap justify-center gap-2.5 mt-10" aria-label={a.titulo} data-testid="assoc-subnav">
                        {subnav.map(([id, label]) => (
                            <button key={id} onClick={() => scrollTo(id)} data-testid={`assoc-subnav-${id}`}
                                className="font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b]/85 border border-[#c9a24b]/30 px-4 py-2 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-300">
                                {label}
                            </button>
                        ))}
                    </nav>
                </Reveal>

                {/* 1 — Quem somos */}
                <section id="quem-somos" className="mt-20 scroll-mt-28" data-testid="assoc-quem-somos">
                    <Reveal><SectionTitle testid="assoc-quem-somos-title">{a.quemSomos.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-7 space-y-5 font-light text-base text-[#efe6d6]/80 leading-relaxed max-w-3xl">
                            <p>{a.quemSomos.p1}</p><p>{a.quemSomos.p2}</p><p>{a.quemSomos.p3}</p>
                        </div>
                    </Reveal>
                </section>

                {/* 2 — Dados institucionais */}
                <section id="dados" className="mt-16 scroll-mt-28" data-testid="assoc-dados">
                    <Reveal><SectionTitle testid="assoc-dados-title">{a.dados.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.1}>
                        <dl className="mt-7 border border-[#c9a24b]/25 bg-[#0a0705]/70" data-testid="assoc-dados-tabela">
                            {linhasDados.map(([label, valor, pendente]) => (
                                <div key={label} className="grid sm:grid-cols-[240px_1fr] border-b border-[#c9a24b]/12 last:border-b-0">
                                    <dt className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#d19a3f] px-5 py-3.5">{label}</dt>
                                    <dd className={`px-5 py-3.5 text-sm leading-relaxed ${pendente ? "font-display italic text-[#efe6d6]/50" : "font-light text-[#efe6d6]/85"}`}
                                        data-testid={`assoc-dado-${label.toLowerCase().replace(/\s+/g, "-")}`}>{valor}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>
                </section>

                {/* 3 — Direção */}
                <section id="direcao" className="mt-16 scroll-mt-28" data-testid="assoc-direcao">
                    <Reveal><SectionTitle testid="assoc-direcao-title">{a.direcao.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.05}>
                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50 mt-7">
                            {a.direcao.comite} · <span className="text-[#c9a24b]">{a.direcao.mandato}</span>
                        </p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-5 mt-5">
                        {MEMBROS_DIRECAO.map((m, i) => <Reveal key={m.nome} delay={0.08 * i}><MembroCard m={m} t={t} /></Reveal>)}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50 mt-9">{a.direcao.fundadores}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-5 mt-5">
                        {MEMBROS_FUNDADORES.map((m, i) => <Reveal key={m.nome} delay={0.08 * i}><MembroCard m={m} t={t} /></Reveal>)}
                    </div>
                    <Reveal delay={0.12}>
                        <p className="font-display italic font-light text-sm text-[#efe6d6]/55 mt-7" data-testid="assoc-direcao-nota">{a.direcao.nota}</p>
                    </Reveal>
                </section>

                {/* 4 — Parceiros */}
                <section id="parceiros" className="mt-16 scroll-mt-28" data-testid="assoc-parceiros">
                    <Reveal><SectionTitle testid="assoc-parceiros-title">{a.parceiros.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.05}>
                        <p className="font-light text-base text-[#efe6d6]/75 leading-relaxed max-w-2xl mt-6">{a.parceiros.intro}</p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50 mt-8">{a.parceiros.institucional}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-4" data-testid="assoc-parceiros-institucionais">
                            {PARCEIROS_INSTITUCIONAIS.map((p) => <ParceiroCard key={p.id} p={p} titulo={a.parceiros[p.tituloKey]} />)}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50 mt-9">{a.parceiros.patrocinadores}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-4" data-testid="assoc-parceiros-patrocinadores">
                            {PATROCINADORES.map((p) => <ParceiroCard key={p.id} p={p} titulo={a.parceiros.tiers[p.tier]} sub={p.descritorKey ? a.parceiros[p.descritorKey] : null} />)}
                        </div>
                    </Reveal>
                </section>

                {/* 5 — Quotas */}
                <section id="quotas" className="mt-16 scroll-mt-28" data-testid="assoc-quotas">
                    <Reveal><SectionTitle testid="assoc-quotas-title">{a.quotas.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-7 space-y-5 max-w-3xl">
                            <p className="font-display font-light text-2xl gold-text" data-testid="assoc-quota-valor">{a.quotas.valor}</p>
                            <p><Link to="/socios/vantagens" className="font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b] underline underline-offset-4 hover:text-[#ecd49b]" data-testid="assoc-vantagens-link">{t.vantagens.titulo}</Link></p>
                            <p className="font-light text-base text-[#efe6d6]/80 leading-relaxed">{a.quotas.isencao}</p>
                            <p className="font-light text-base text-[#efe6d6]/80 leading-relaxed">
                                <span className="text-[#efe6d6]">{a.quotas.comoPagar.split(".")[0]}.</span>{" "}
                                {a.quotas.comoPagar.split(". ").slice(1).join(". ").replace("{famosa}", famosa)}
                            </p>
                            <p className="font-light text-base text-[#efe6d6]/80 leading-relaxed">
                                <span className="text-[#efe6d6]">{a.quotas.admissao.split(".")[0]}.</span>{" "}
                                {a.quotas.admissao.split(". ").slice(1).join(". ")}
                            </p>
                            <div className="border border-[#c9a24b]/25 bg-[#0a0705]/70 p-6 mt-2" data-testid="assoc-categorias">
                                <p className="font-caps text-[10px] tracking-[0.28em] uppercase text-[#d19a3f]">{a.quotas.categoriasTitulo}</p>
                                <ul className="mt-4 space-y-2.5">
                                    {[a.quotas.fundadores, a.quotas.efetivos, a.quotas.honorarios, a.quotas.apoiantes].map((c) => (
                                        <li key={c} className="font-light text-sm text-[#efe6d6]/75 leading-relaxed flex gap-3">
                                            <span className="text-[#c9a24b] shrink-0">—</span>{c}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* 6 — Pedido de adesão */}
                <section id="adesao" className="mt-16 scroll-mt-28" data-testid="assoc-adesao">
                    <Reveal><SectionTitle testid="assoc-adesao-title">{a.form.titulo}</SectionTitle></Reveal>
                    {estado === "sucesso" ? (
                        <Reveal delay={0.1}>
                            <div className="border border-[#c9a24b]/40 bg-[#0a0705]/80 p-10 text-center mt-8" data-testid="assoc-adesao-sucesso">
                                <CircleCheck size={44} strokeWidth={1.2} className="text-[#c9a24b] mx-auto" />
                                <p className="font-light text-[#efe6d6]/85 mt-6 leading-relaxed max-w-xl mx-auto">{a.form.sucesso}</p>
                            </div>
                        </Reveal>
                    ) : (
                        <Reveal delay={0.1}>
                            <form onSubmit={submeter} className="border border-[#c9a24b]/30 bg-[#0a0705]/80 p-7 sm:p-10 mt-8 space-y-6" data-testid="assoc-adesao-form" noValidate>
                                {/* honeypot anti-spam */}
                                <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
                                    aria-hidden="true" className="hidden" style={{ position: "absolute", left: "-9999px" }} />
                                <Campo label={a.form.nome} erro={erros.nome} testid="assoc-campo-nome">
                                    <label className={labelCls}>{a.form.nome} *</label>
                                    <input type="text" value={form.nome} onChange={set("nome")} className={inputCls} data-testid="assoc-nome" />
                                </Campo>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Campo label={a.form.rua} erro={erros.rua} testid="assoc-campo-rua">
                                        <label className={labelCls}>{a.form.rua} *</label>
                                        <input type="text" value={form.rua} onChange={set("rua")} className={inputCls} data-testid="assoc-rua" />
                                    </Campo>
                                    <div className="grid grid-cols-2 gap-6">
                                        <Campo label={a.form.cp} erro={erros.cp} testid="assoc-campo-cp">
                                            <label className={labelCls}>{a.form.cp} *</label>
                                            <input type="text" value={form.cp} onChange={set("cp")} className={inputCls} data-testid="assoc-cp" />
                                        </Campo>
                                        <Campo label={a.form.localidade} erro={erros.localidade} testid="assoc-campo-localidade">
                                            <label className={labelCls}>{a.form.localidade} *</label>
                                            <input type="text" value={form.localidade} onChange={set("localidade")} className={inputCls} data-testid="assoc-localidade" />
                                        </Campo>
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Campo label={a.form.pais} erro={erros.pais} testid="assoc-campo-pais">
                                        <label className={labelCls}>{a.form.pais} *</label>
                                        <input type="text" value={form.pais} onChange={set("pais")} className={inputCls} data-testid="assoc-pais" />
                                    </Campo>
                                    <Campo label={a.form.email} erro={erros.email} testid="assoc-campo-email">
                                        <label className={labelCls}>{a.form.email} *</label>
                                        <input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="assoc-email" />
                                    </Campo>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className={labelCls}>{a.form.telefone} <span className="text-[#efe6d6]/40 normal-case tracking-normal">({a.form.telefoneOpc})</span></label>
                                        <input type="tel" value={form.telefone} onChange={set("telefone")} className={inputCls} data-testid="assoc-telefone" />
                                    </div>
                                    <div>
                                        <label className={labelCls}>{a.form.categoria} *</label>
                                        <select value={form.categoria} onChange={set("categoria")} className={inputCls} data-testid="assoc-categoria">
                                            <option value="efetivo">{a.form.catEfetivo}</option>
                                            <option value="apoiante">{a.form.catApoiante}</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>{a.form.comprovativo} *</label>
                                    <label htmlFor="assoc-ficheiro"
                                        className="flex items-center justify-center gap-3 border border-dashed border-[#c9a24b]/40 px-5 py-6 cursor-pointer hover:border-[#c9a24b] transition-colors duration-300"
                                        data-testid="assoc-ficheiro-label">
                                        <Upload size={16} className="text-[#c9a24b]" />
                                        <span className="font-light text-sm text-[#efe6d6]/70">
                                            {ficheiro ? ficheiro.name : `${a.form.escolherFicheiro} · ${a.form.comprovativoNota}`}
                                        </span>
                                    </label>
                                    <input id="assoc-ficheiro" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={escolherFicheiro} className="hidden" data-testid="assoc-ficheiro" />
                                    {erros.comprovativo && (
                                        <p className="font-light text-xs text-[#e0a0a0] mt-1.5 flex items-center gap-1.5" data-testid="assoc-ficheiro-erro">
                                            <CircleAlert size={12} /> {erros.comprovativo}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-4">
                                    {[
                                        [aceite1, setAceite1, a.form.aceiteEstatutos, a.form.verEstatutos, "estatutos", erros.aceite1, "assoc-aceite-estatutos"],
                                        [aceite2, setAceite2, a.form.aceiteDados, a.form.verNota, "protecao-dados", erros.aceite2, "assoc-aceite-dados"],
                                    ].map(([val, setter, texto, linkTexto, alvo, erro, testid]) => (
                                        <div key={testid}>
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input type="checkbox" checked={val} onChange={(e) => setter(e.target.checked)}
                                                    className="mt-1 accent-[#c9a24b] w-4 h-4 shrink-0" data-testid={testid} />
                                                <span className="font-light text-sm text-[#efe6d6]/75 leading-relaxed">
                                                    {texto}{" "}
                                                    <button type="button" onClick={() => scrollTo(alvo)} data-testid={`${testid}-link`}
                                                        className="text-[#c9a24b] underline underline-offset-4 decoration-[#c9a24b]/40 hover:decoration-[#c9a24b]">
                                                        ({linkTexto})
                                                    </button>
                                                </span>
                                            </label>
                                            {erro && <p className="font-light text-xs text-[#e0a0a0] mt-1.5 flex items-center gap-1.5" data-testid={`${testid}-erro`}><CircleAlert size={12} /> {erro}</p>}
                                        </div>
                                    ))}
                                </div>
                                {erroGeral && <p className="font-light text-sm text-[#e0a0a0]" data-testid="assoc-erro-geral">{erroGeral}</p>}
                                <button type="submit" disabled={estado === "aEnviar"} data-testid="assoc-enviar"
                                    className="w-full font-caps text-xs tracking-[0.3em] uppercase bg-[#7B1D2A] text-white border border-[#c9a24b]/60 px-8 py-4 hover:bg-[#8f2433] transition-colors duration-400 disabled:opacity-50">
                                    {estado === "aEnviar" ? a.form.aEnviar : a.form.enviar}
                                </button>
                            </form>
                        </Reveal>
                    )}
                </section>

                {/* 7 — Estatutos */}
                <section id="estatutos" className="mt-16 scroll-mt-28" data-testid="assoc-estatutos">
                    <Reveal><SectionTitle testid="assoc-estatutos-title">{a.estatutos.titulo}</SectionTitle></Reveal>
                    <Reveal delay={0.05}>
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
                            <div>
                                <p className="font-display font-light text-lg text-[#efe6d6]">{est.titulo}</p>
                                <p className="font-display italic font-light text-sm text-[#efe6d6]/55 mt-1">{est.subtitulo}</p>
                                {est.notaTraducao && (
                                    <p className="font-display italic font-light text-sm text-[#c9a24b]/90 mt-2" data-testid="assoc-estatutos-nota">{est.notaTraducao}</p>
                                )}
                            </div>
                            <a href={`${API}/estatutos.pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="assoc-estatutos-pdf"
                                className="inline-flex items-center gap-2.5 font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3 hover:bg-[#ecd49b] transition-colors duration-300">
                                <Download size={14} /> {a.estatutos.descarregar}
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50 mt-8">{a.estatutos.indice}</p>
                        <div className="flex flex-wrap gap-2 mt-3" data-testid="assoc-estatutos-indice">
                            {est.capitulos.map((c, i) => (
                                <button key={c.titulo} onClick={() => { setAberto(i); setTimeout(() => scrollTo(`est-cap-${i}`), 60); }}
                                    data-testid={`assoc-indice-cap-${i}`}
                                    className="font-light text-xs text-[#c9a24b]/85 border border-[#c9a24b]/25 px-3 py-1.5 hover:bg-[#c9a24b]/10 transition-colors duration-300">
                                    {c.titulo.split("—")[0].trim()}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-6 border border-[#c9a24b]/25 bg-[#0a0705]/70" data-testid="assoc-estatutos-acordeao">
                            {est.capitulos.map((cap, i) => (
                                <div key={cap.titulo} id={`est-cap-${i}`} className="border-b border-[#c9a24b]/12 last:border-b-0 scroll-mt-28">
                                    <button onClick={() => setAberto(aberto === i ? -1 : i)} data-testid={`assoc-cap-${i}`}
                                        className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 text-left">
                                        <span className="font-caps text-[11px] sm:text-xs tracking-[0.18em] uppercase text-[#d19a3f]">{cap.titulo}</span>
                                        <ChevronDown size={16} className={`text-[#c9a24b] shrink-0 transition-transform duration-300 ${aberto === i ? "rotate-180" : ""}`} />
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {aberto === i && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-5 sm:px-7 pb-7 space-y-5">
                                                    {cap.artigos.map((art) => (
                                                        <article key={art.num + art.titulo} data-testid={`assoc-artigo-${art.num.toLowerCase().replace(/[\s.º]/g, "")}`}>
                                                            <h3 className="font-display font-light text-base text-[#efe6d6]">
                                                                {art.num} — {art.titulo}{art.paragrafos.length > 0 ? "." : ""}
                                                            </h3>
                                                            {art.paragrafos.map((p, j) => (
                                                                <p key={j} className="font-light text-sm text-[#efe6d6]/75 leading-relaxed mt-2">{p}</p>
                                                            ))}
                                                        </article>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <p className="font-display italic font-light text-sm text-[#efe6d6]/55 mt-7" data-testid="assoc-estatutos-aprovacao">{est.aprovacao}</p>
                    </Reveal>
                </section>

                {/* 8 — Proteção de dados */}
                <section id="protecao-dados" className="mt-16 scroll-mt-28" data-testid="assoc-protecao">
                    <Reveal>
                        <div className="border-t border-[#c9a24b]/20 pt-8">
                            <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f]" data-testid="assoc-protecao-title">{a.protecao.titulo}</p>
                            <p className="font-light text-sm text-[#efe6d6]/65 leading-relaxed mt-4 max-w-3xl">{a.protecao.texto}</p>
                        </div>
                    </Reveal>
                </section>
            </motion.main>
        </div>
    );
}
